<app-page-shell>
    <p-toast position="bottom-center"></p-toast>
    <p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

    <app-page-hero eyebrow="Ledgers" title="Partner Ledger"
        subtitle="Review AR/AP partner movement in grouped audit rows with backend-owned balances.">
        <div actions class="ledger-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="applyFilters()"></p-button>
            <p-button label="Reset" icon="pi pi-filter-slash" severity="secondary" [outlined]="true"
                (onClick)="clearFilters()"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true"
                (onClick)="printReport()"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel *ngIf="diagnostics.length > 0" title="Report Diagnostics"
        [resultText]="diagnostics.length + ' issue(s)'">
        <div class="diagnostic-list">
            <div *ngFor="let diagnostic of diagnostics" class="diagnostic-item">
                <div class="diagnostic-title">
                    <span>{{ diagnostic.code }}</span>
                    <p-tag [value]="diagnostic.severity" [severity]="diagnosticSeverity(diagnostic.severity)"></p-tag>
                </div>
                <div>{{ diagnostic.message }}</div>
                <small *ngIf="diagnostic.referenceNumber">Reference: {{ diagnostic.referenceNumber }}</small>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-form-section icon="pi pi-filter" title="Partner Ledger Filters"
        subtitle="Filter by partner, period, control account behavior, and source fields.">
        <div class="ledger-filter-grid">
            <p-iftalabel>
                <p-datePicker inputId="partnerDateRange" [(ngModel)]="filterDateRange" selectionMode="range"
                    [readonlyInput]="true" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="partnerDateRange">Transaction Date Range</label>
            </p-iftalabel>

            <p-iftalabel>
                <p-multiSelect inputId="partnerTypes" [(ngModel)]="filterPartnerTypes" [options]="partnerTypeOptions"
                    display="chip" (onChange)="onPartnerTypeChange()" styleClass="w-full"></p-multiSelect>
                <label for="partnerTypes">Partner Types</label>
            </p-iftalabel>

            <p-iftalabel>
                <p-multiSelect inputId="partners" [(ngModel)]="selectedPartnerOptions"
                    [options]="visiblePartnerOptions" optionLabel="label" [filter]="true" filterBy="label"
                    display="chip" styleClass="w-full"></p-multiSelect>
                <label for="partners">Partners</label>
            </p-iftalabel>

            <div class="filter-command-bar">
                <p-button label="Apply" icon="pi pi-filter" (onClick)="applyFilters()"></p-button>
                <p-button icon="pi pi-filter-slash" severity="secondary" [outlined]="true" pTooltip="Clear filters"
                    (onClick)="clearFilters()"></p-button>
            </div>
        </div>

        <details class="ledger-advanced-filters">
            <summary>
                <i class="pi pi-sliders-h"></i>
                Advanced filters
            </summary>
            <div class="ledger-filter-grid advanced">
                <p-iftalabel>
                    <p-select inputId="accountRole" [(ngModel)]="filterAccountRole" [options]="accountRoleOptions"
                        optionLabel="label" optionValue="value" [showClear]="true" styleClass="w-full"></p-select>
                    <label for="accountRole">Account Role</label>
                </p-iftalabel>

                <p-iftalabel>
                    <p-multiSelect inputId="currencyCodes" [(ngModel)]="filterCurrencyCodes"
                        [options]="currencyOptions" display="chip" styleClass="w-full"></p-multiSelect>
                    <label for="currencyCodes">Currency</label>
                </p-iftalabel>

                <p-iftalabel>
                    <p-multiSelect inputId="journals" [(ngModel)]="filterJournals" [options]="journalOptions"
                        display="chip" styleClass="w-full"></p-multiSelect>
                    <label for="journals">Journals</label>
                </p-iftalabel>

                <p-iftalabel>
                    <app-logistics-project-lookup inputId="logisticsProject" [(ngModel)]="filterLogisticsProjectId"></app-logistics-project-lookup>
                    <label for="logisticsProject">Logistics Project</label>
                </p-iftalabel>

                <p-iftalabel>
                    <input id="transactionNumber" type="text" pInputText [(ngModel)]="filterTransactionNumber" />
                    <label for="transactionNumber">Transaction #</label>
                </p-iftalabel>

                <p-iftalabel>
                    <input id="reference" type="text" pInputText [(ngModel)]="filterReference" />
                    <label for="reference">Reference</label>
                </p-iftalabel>

                <p-iftalabel>
                    <input id="description" type="text" pInputText [(ngModel)]="filterDescription" />
                    <label for="description">Description</label>
                </p-iftalabel>
            </div>
        </details>
    </app-page-form-section>

    <app-page-data-panel title="Partner Balances"
        subtitle="Each row is a partner and currency balance; expand to inspect source ledger movement."
        [resultText]="selectedPartnerLedgerSummaries.length + ' partner balances'">
        <p-table [value]="selectedPartnerLedgerSummaries" dataKey="partnerKey" responsiveLayout="scroll"
            styleClass="p-datatable-gridlines p-datatable-sm ledger-audit-table" [tableStyle]="{ 'min-width': '78rem' }"
            [paginator]="true" [rows]="rows" [first]="first" [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} partner balances"
            [rowsPerPageOptions]="[10, 20, 50]" [resizableColumns]="true" columnResizeMode="fit">
            <ng-template pTemplate="header">
                <tr>
                    <th>Partner</th>
                    <th class="numeric amount-col">Opening</th>
                    <th class="numeric amount-col">Debit</th>
                    <th class="numeric amount-col">Credit</th>
                    <th class="numeric amount-col">Closing</th>
                    <th class="numeric amount-col">Open Balance</th>
                    <th class="numeric count-col">Entries</th>
                    <th class="actions-col">Actions</th>
                </tr>
            </ng-template>

            <ng-template pTemplate="body" let-summary let-expanded="expanded">
                <tr>
                    <td>
                        <div class="cell-stack partner-cell">
                            <strong>{{ summary.partnerName }}</strong>
                            <div class="tag-row">
                                <p-tag [value]="summary.partnerType || 'Partner'" severity="info"></p-tag>
                                <p-tag [value]="summary.currencyCode || 'Currency'" severity="secondary"></p-tag>
                                <p-tag *ngFor="let role of summary.accountRoles" [value]="role"
                                    [severity]="accountRoleSeverity(role)"></p-tag>
                            </div>
                        </div>
                    </td>
                    <td class="numeric amount balance">
                        {{ summary.openingBalance | currency:summary.currencyCode:'symbol-narrow':'1.2-2' }}
                    </td>
                    <td class="numeric amount debit">
                        {{ summary.totalDebit | currency:summary.currencyCode:'symbol-narrow':'1.2-2' }}
                    </td>
                    <td class="numeric amount credit">
                        {{ summary.totalCredit | currency:summary.currencyCode:'symbol-narrow':'1.2-2' }}
                    </td>
                    <td class="numeric amount balance">
                        {{ summary.closingBalance | currency:summary.currencyCode:'symbol-narrow':'1.2-2' }}
                    </td>
                    <td class="numeric amount balance">
                        {{ summary.openBalance | currency:summary.currencyCode:'symbol-narrow':'1.2-2' }}
                    </td>
                    <td class="numeric">{{ summary.totalRowCount }}</td>
                    <td class="actions-col">
                        <div class="row-actions">
                            <p-button [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" [text]="true"
                                [rounded]="true" severity="secondary" [pRowToggler]="summary"
                                pTooltip="Toggle partner lines"></p-button>
                            <p-button icon="pi pi-file-excel" [text]="true" [rounded]="true" severity="success"
                                pTooltip="Export partner" (onClick)="exportPartnerExcel(summary)"></p-button>
                            <p-button icon="pi pi-print" [text]="true" [rounded]="true" severity="secondary"
                                pTooltip="Print partner" (onClick)="printPartnerReport(summary)"></p-button>
                        </div>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="expandedrow" let-summary>
                <tr>
                    <td colspan="8" class="ledger-row-expansion">
                        <div class="partner-summary-strip">
                            <span>{{ summary.partnerName }}</span>
                            <span>{{ summary.totalRowCount }} entries</span>
                            <span>{{ summary.accountLabels.join(' | ') || 'No control account label' }}</span>
                        </div>

                        <p-table [value]="summary.partnerLedgers" dataKey="journalItemId" responsiveLayout="scroll"
                            styleClass="p-datatable-gridlines p-datatable-sm partner-line-table"
                            [tableStyle]="{ 'min-width': '70rem' }" [paginator]="true" [rows]="15"
                            [rowsPerPageOptions]="[15, 30, 60]">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th class="date-col">Date</th>
                                    <th>Document</th>
                                    <th>Account</th>
                                    <th class="numeric amount-col">Debit</th>
                                    <th class="numeric amount-col">Credit</th>
                                    <th class="numeric amount-col">Balance</th>
                                    <th class="actions-col">Actions</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-ledger let-expanded="expanded">
                                <tr>
                                    <td>
                                        <strong>{{ ledger.transactionDate | date:'yyyy-MM-dd' }}</strong>
                                    </td>
                                    <td>
                                        <div class="cell-stack">
                                            <strong>{{ ledger.transactionNumber || ledger.sourceDocumentNumber || '-' }}</strong>
                                            <span class="cell-meta">
                                                <i class="pi pi-book"></i>
                                                {{ ledger.journal || '-' }}
                                            </span>
                                            <span class="cell-meta" *ngIf="ledger.reference">
                                                <i class="pi pi-bookmark"></i>
                                                {{ ledger.reference }}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="cell-stack">
                                            <strong>{{ ledger.accountCode || '-' }}</strong>
                                            <span>{{ ledger.accountName || '-' }}</span>
                                            <p-tag *ngIf="ledger.accountRole" [value]="ledger.accountRole"
                                                [severity]="accountRoleSeverity(ledger.accountRole)"></p-tag>
                                        </div>
                                    </td>
                                    <td class="numeric amount debit">
                                        {{ ledger.debit | currency:rowCurrency(ledger, summary):'symbol-narrow':'1.2-2' }}
                                    </td>
                                    <td class="numeric amount credit">
                                        {{ ledger.credit | currency:rowCurrency(ledger, summary):'symbol-narrow':'1.2-2' }}
                                    </td>
                                    <td class="numeric amount balance">
                                        {{ ledger.balance | currency:rowCurrency(ledger, summary):'symbol-narrow':'1.2-2' }}
                                    </td>
                                    <td class="actions-col">
                                        <p-button [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
                                            [text]="true" [rounded]="true" severity="secondary" [pRowToggler]="ledger"
                                            pTooltip="Toggle source details"></p-button>
                                    </td>
                                </tr>
                            </ng-template>

                            <ng-template pTemplate="expandedrow" let-ledger>
                                <tr>
                                    <td colspan="7" class="line-detail-row">
                                        <div class="detail-grid">
                                            <div>
                                                <span class="detail-label">Description</span>
                                                <strong>{{ ledger.description || '-' }}</strong>
                                            </div>
                                            <div>
                                                <span class="detail-label">Source</span>
                                                <strong>{{ sourceLabel(ledger) }}</strong>
                                            </div>
                                            <div>
                                                <span class="detail-label">Reference</span>
                                                <strong>{{ ledger.reference || '-' }}</strong>
                                            </div>
                                            <div>
                                                <span class="detail-label">Journal Entry ID</span>
                                                <strong>{{ ledger.journalEntryId || '-' }}</strong>
                                            </div>
                                            <div>
                                                <span class="detail-label">Journal Line ID</span>
                                                <strong>{{ ledger.journalItemId || '-' }}</strong>
                                            </div>
                                            <div>
                                                <span class="detail-label">Partner Type</span>
                                                <strong>{{ ledger.partnerType || '-' }}</strong>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </ng-template>

                            <ng-template pTemplate="emptymessage">
                                <tr>
                                    <td colspan="7" class="empty-cell">No partner ledger entries found.</td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="8" class="empty-cell">No partner balances found for the selected criteria.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>
